import { useState, useEffect, useCallback, useRef } from 'react';
import API_ENDPOINTS from '../config/api';
import { SCENARIO_COLORS } from '../config/brandColors';
import { generateDemoData } from '../utils/pivotDemoData';

/**
 * Data layer for PivotPointsScreen — extracted so the screen stays focused on
 * view state + rendering. Owns all backend fetching, scenario/node transforms,
 * RAG cluster loading, and demo/fallback data.
 *
 * AI discovery is the module's admin action (pivot_points_v1.discover job).
 *
 * Also exposes rawPathways + connections (the untransformed impact-path DAG)
 * which the structural (MICMAC) analysis method consumes — these are NOT in the
 * transformed `nodes` (which drops scenario starters).
 *
 * @param {object} args
 * @param {URLSearchParams} args.searchParams
 * @param {object} args.currentInstance
 */
export function usePivotData({ searchParams, currentInstance }) {
  const [scenarios, setScenarios] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [pivotPoints, setPivotPoints] = useState([]); // Semantic pivot points from RAG
  const [clusteredNodeIds, setClusteredNodeIds] = useState(new Set());
  const [rawPathways, setRawPathways] = useState([]); // Untransformed DAG nodes (for structural analysis)
  const [connections, setConnections] = useState([]); // Multi-parent connections (for structural analysis)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [pivotReports, setPivotReports] = useState({});

  const hasLoadedOnce = useRef(false);
  const lastFetchedId = useRef(null);

  // Fetch data from backend
  const fetchData = useCallback(async () => {
    // Only show loading spinner on very first load — hide re-fetch glitches
    if (!hasLoadedOnce.current) setLoading(true);
    setError(null);

    const instanceId = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId') || currentInstance?.id;
    const demoParam = searchParams.get('demo');

    // Use demo mode if param is set or no instance
    if (demoParam === 'true' || !instanceId) {
      const demoData = generateDemoData();
      setScenarios(demoData.scenarios);
      setNodes(demoData.nodes);
      setRawPathways([]);
      setConnections([]);
      setIsDemoMode(true);
      setLoading(false);
      return;
    }

    try {
      // Fetch all pathway nodes for the instance (includes scenario starters and user submissions)
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const [submissionsResponse, scenariosResponse] = await Promise.all([
        fetch(`${socketUrl}/api/pathways?kid=${instanceId}`),
        fetch(API_ENDPOINTS.SCENARIOS_WITH_VARIABLES(instanceId)),
      ]);
      if (!submissionsResponse.ok) throw new Error('Failed to fetch submissions');
      const submissionsData = await submissionsResponse.json();

      // The API returns { pathways: [...], count: N, ... }
      const pathways = submissionsData.pathways || submissionsData || [];

      let dbScenarios = [];
      if (scenariosResponse.ok) {
        const scData = await scenariosResponse.json();
        dbScenarios = Array.isArray(scData) ? scData : (scData.scenarios || []);
      }
      const dbById = new Map(dbScenarios.map(s => [s.id, s]));
      const dbByTitle = new Map(dbScenarios.map(s => [s.title, s]));
      // with-variables is already ordered by order_index; use array position as fallback
      // until older deployments include the order_index field in the response.
      const dbOrderById = new Map(dbScenarios.map((s, i) => [s.id, i]));
      const dbOrderByTitle = new Map(dbScenarios.map((s, i) => [s.title, i]));

      // Build scenario data from scenario_root entries in pathways
      // Group by MAIN scenario name (before ":") to avoid duplicate edges
      const scenarioRoots = pathways.filter(p => p.type === 'scenario_root');
      // Muuttujat are starters hanging directly off a scenario root. User-created
      // ones keep type 'human' (see impactPathMutations), so do not key off type.
      const scenarioRootIds = new Set(scenarioRoots.map(p => p.id));
      const isVariableNode = (p) =>
        p.is_scenario_starter === true && p.parentId && scenarioRootIds.has(p.parentId);
      const scenarioVariables = pathways.filter(isVariableNode);

      // Prefer DB color/icon from trend catalogs; fall back to brandColors for legacy Suomi2050
      const scenarioIcons = ['🏛️', '🌍', '💻', '👥'];

      // Group roots by main scenario name (e.g., "Demokratia: Variant" → "Demokratia")
      const scenarioGroups = new Map();
      scenarioRoots.forEach(root => {
        // Extract main scenario name (before colon, or full text if no colon)
        const mainName = root.text.includes(':') ? root.text.split(':')[0].trim() : root.text;
        if (!scenarioGroups.has(mainName)) {
          scenarioGroups.set(mainName, {
            id: root.scenarioId,
            roots: [],
            variableIds: new Set()
          });
        }
        scenarioGroups.get(mainName).roots.push(root);
      });

      // Build alias map: duplicate variable IDs → canonical variable ID
      // This ensures nodes connected to ANY duplicate ID will find the variable
      const variableIdAlias = new Map();

      // Build unique scenarios with deduplicated variables
      const transformedScenarios = Array.from(scenarioGroups.entries()).map(([mainName, group], idx) => {
        // Collect all variables for this scenario group, deduplicated by text
        const seenVarTexts = new Map(); // text → canonical ID
        const variables = [];

        scenarioVariables
          .filter(v => group.roots.some(r => v.parentId === r.id || v.scenarioId === r.scenarioId))
          .forEach(v => {
            if (!seenVarTexts.has(v.text)) {
              // First occurrence - this becomes the canonical ID
              seenVarTexts.set(v.text, v.id);
              variables.push({ id: v.id, title: v.text });
              variableIdAlias.set(v.id, v.id); // Canonical maps to itself
            } else {
              // Duplicate - map to canonical ID
              const canonicalId = seenVarTexts.get(v.text);
              variableIdAlias.set(v.id, canonicalId);
            }
          });

        const dbScenario = dbById.get(group.id) || dbByTitle.get(mainName);
        const isTrend = dbScenario?.catalog_mode === 'trend'
          || (dbScenario?.scenario_type && String(dbScenario.scenario_type).startsWith('trend:'));

        return {
          id: group.id,
          title: mainName, // Use main scenario name, not "Demokratia: Variant"
          icon: dbScenario?.icon || scenarioIcons[idx % 4],
          color: dbScenario?.color
            || SCENARIO_COLORS[mainName]
            || SCENARIO_COLORS[mainName.toLowerCase()]
            || '#607d8b',
          orderIndex: dbScenario?.order_index
            ?? dbOrderById.get(group.id)
            ?? dbOrderByTitle.get(mainName)
            ?? idx,
          isTrend: Boolean(isTrend),
          variables
        };
      });

      // Trend catalogs: keep DB order_index. Legacy Suomi2050: fixed edge order.
      const hasTrendCatalog = transformedScenarios.some(s => s.isTrend);
      if (hasTrendCatalog) {
        transformedScenarios.sort((a, b) => (a.orderIndex ?? 99) - (b.orderIndex ?? 99));
      } else {
        const SCENARIO_EDGE_ORDER = ['Demokratia', 'Väestö', 'Teknologia', 'Ilmasto'];
        transformedScenarios.sort((a, b) => {
          const ai = SCENARIO_EDGE_ORDER.indexOf(a.title);
          const bi = SCENARIO_EDGE_ORDER.indexOf(b.title);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
      }

      console.log(`Built variable alias map with ${variableIdAlias.size} entries`);

      // Build lookup map for all pathways by ID
      const pathwayById = new Map();
      pathways.forEach(p => pathwayById.set(p.id, p));

      // Identify scenario variables (starters under a scenario_root)
      // These are what user submissions should connect to
      const scenarioVariableIds = new Set();
      pathways.forEach(p => {
        if (isVariableNode(p)) {
          scenarioVariableIds.add(p.id);
        }
      });

      console.log(`Found ${scenarioVariableIds.size} scenario variables (not roots)`);

      // Helper: trace back through parent chain to find the ancestor scenario variable
      const findAncestorVariable = (nodeId) => {
        let current = pathwayById.get(nodeId);
        const visited = new Set();

        while (current && current.parentId && !visited.has(current.id)) {
          visited.add(current.id);

          // If parent is a scenario variable, we found it
          if (scenarioVariableIds.has(current.parentId)) {
            return current.parentId;
          }

          // Move up to parent
          current = pathwayById.get(current.parentId);
        }

        return null;
      };

      // Transform user submissions to nodes format for visualization
      // Each submission connects to its ancestor scenario variable (traced through parent chain)
      // Filter out scenario starters - we only want user submissions
      const transformedNodes = pathways
        .filter(sub => !sub.is_scenario_starter && sub.parentId)
        .map(sub => {
          // Trace back to find which scenario variable this node ultimately connects to
          let ancestorVariableId = sub.parentId;

          // If parent is not a scenario variable, trace back
          if (!scenarioVariableIds.has(sub.parentId)) {
            ancestorVariableId = findAncestorVariable(sub.id);
          }

          // Map to canonical variable ID (handles duplicates)
          const canonicalVarId = ancestorVariableId ? variableIdAlias.get(ancestorVariableId) || ancestorVariableId : null;
          const connectedVariables = canonicalVarId ? [canonicalVarId] : [];

          // For "all nodes" view, each submission connects to ONE variable (its ancestor)
          const isPivotPoint = false; // Individual submissions are not pivot points

          return {
            id: sub.id,
            text: sub.text || sub.title || 'Untitled',
            connectedVariables,
            participant: sub.participantName || sub.created_by || 'Osallistuja',
            size: 1,
            isCrossScenario: isPivotPoint,
            scenarioCount: 1,
            scenarioId: sub.scenarioId,
            parentId: sub.parentId, // Keep parent-child relationship for tree view
            depth: sub.depth || 0   // Tree depth level
          };
        });

      const orphanCount = transformedNodes.filter(n => n.connectedVariables.length === 0).length;
      if (orphanCount > 0) {
        console.warn(`⚠️ ${orphanCount} orphan nodes with no variable connection (broken parent chain)`);
      }
      console.log(`Loaded ${transformedNodes.length} nodes for pivot points view (${orphanCount} orphans)`);

      // Fetch cluster memberships from RAG service for the "Grouped" view
      // We use cluster-memberships as the single source of truth for both:
      // 1. Which nodes are in clusters (to filter unclustered ones)
      // 2. The cluster data itself (to render as pivot points)
      let ragClusters = [];
      let ragClusteredIds = new Set();
      let ragReports = {};
      let ragError = null;
      try {
        const ragRes = await fetch(API_ENDPOINTS.RAG_CLUSTER_MEMBERSHIPS(instanceId));
        // A refusal is not "no pivots yet" (#1211): say so instead of showing an empty list.
        if (!ragRes.ok) {
          const err = new Error(`cluster-memberships HTTP ${ragRes.status}`);
          err.status = ragRes.status;
          throw err;
        }
        const membershipData = await ragRes.json();
        const clusters = membershipData.clusters || [];
        console.log(`📊 Loaded ${clusters.length} clusters from RAG`);

        // Build lookup for nodes by ID to find their connectedVariables
        const nodeById = new Map();
        transformedNodes.forEach(n => nodeById.set(n.id, n));

        // Transform clusters to the format expected by PivotPointsView
        ragClusters = clusters.map(cluster => {
          const memberCount = cluster.size || cluster.memberIds?.length || 0;
          const memberIds = cluster.memberIds || [];

          const aggregatedVariables = [];
          memberIds.forEach(memberId => {
            const memberNode = nodeById.get(memberId);
            if (memberNode?.connectedVariables?.length > 0) {
              memberNode.connectedVariables.forEach(varId => {
                aggregatedVariables.push(varId);
              });
            }
          });

          const variableCounts = {};
          aggregatedVariables.forEach(varId => {
            variableCounts[varId] = (variableCounts[varId] || 0) + 1;
          });

          return {
            id: cluster.clusterId,
            text: cluster.centroidText || 'Klusteri',
            description: cluster.description || '',
            challengeSeverity: cluster.challengeSeverity ?? 3,
            solutionOptimism: cluster.solutionOptimism ?? 3,
            discoveryType: cluster.discoveryType || null,
            leverageActor: cluster.leverageActor || null,
            leverageEffect: cluster.leverageEffect || null,
            isRootCause: cluster.isRootCause ?? null,
            connectedVariables: aggregatedVariables,
            connectedVariableCounts: variableCounts,
            participant: cluster.isCrossScenario ? 'Vipupiste' : 'Klusteri',
            size: memberCount,
            isCrossScenario: cluster.isCrossScenario || false,
            scenarioCount: cluster.scenarioNames?.length || 1,
            scenarioNames: cluster.scenarioNames || [],
            scenarioDistribution: cluster.scenarioDistribution || {},
            importanceScore: cluster.importanceScore,
            memberCount: memberCount,
            memberIds: memberIds,
            report: cluster.report || null
          };
        });

        ragClusteredIds = new Set(Object.keys(membershipData.nodeToCluster || {}));
        ragClusters.forEach(pivot => {
          if (pivot.report) ragReports[pivot.id] = pivot.report;
        });
      } catch (ragErr) {
        console.error('Pivot points could not be loaded from the RAG service:', ragErr.message);
        ragError = ragErr.status === 401 || ragErr.status === 403
          ? 'pivotPoints.errors.accessDenied'
          : 'pivotPoints.errors.clustersFailed';
      }

      // Batch all state updates together — single render
      setScenarios(transformedScenarios);
      setNodes(transformedNodes);
      setRawPathways(pathways);
      setConnections(submissionsData.connections || []);
      setIsDemoMode(false);
      setClusteredNodeIds(ragClusteredIds);
      setPivotPoints(ragClusters.filter(p => (p.memberIds?.length || p.size || 0) > 0));
      if (Object.keys(ragReports).length > 0) setPivotReports(ragReports);
      setError(ragError);
    } catch (err) {
      console.error('Error fetching pivot points data:', err);
      setError('pivotPoints.errors.loadFailed');
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
    }
  }, [searchParams, currentInstance]);

  // Derive stable instanceId string — useEffect depends on this, not fetchData
  const instanceId = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId') || currentInstance?.id;

  useEffect(() => {
    const key = instanceId || '__demo__';
    if (lastFetchedId.current === key) return;
    lastFetchedId.current = key;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  return {
    scenarios,
    nodes,
    pivotPoints,
    setPivotPoints,
    clusteredNodeIds,
    rawPathways,
    connections,
    pivotReports,
    setPivotReports,
    isDemoMode,
    loading,
    error,
    refetch: fetchData,
  };
}
